import { useState, useRef, useCallback, useEffect } from "react";
import { toPng, toBlob, getFontEmbedCSS } from "html-to-image";
import { Download, Maximize2, X, User, ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { 
  ShareProfileCard, 
  ProfileCardFormat,
  PROFILE_POST_WIDTH, 
  PROFILE_STORY_WIDTH,
  calculateProfileCardHeight,
} from "./ShareProfileCard";
import shareCardBg from "@/assets/share-card-bg.png";
import { useConfetti } from "@/hooks/useConfetti";
import { saveGraphicDownload } from "@/hooks/useGraphicDownloads";
import { dataUrlToPngBlobChunked, waitForImages } from "@/components/admin/instagram/exportUtils";
import { imageToDataUrl } from "@/hooks/useImageDataUrl";
import { composePngOnBackground } from "@/lib/export/compositePng";
import type { Shelter } from "@/types/shelter";

interface ShareProfileModalProps {
  shelter: Shelter;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

interface IosSaveState {
  filename: string;
  file: File;
  blobUrl: string;
  shareSupported: boolean;
}

const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
};

export const ShareProfileModal = ({ shelter, isOpen, onOpenChange }: ShareProfileModalProps) => {
  const [format, setFormat] = useState<ProfileCardFormat>("post");
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewScale, setPreviewScale] = useState(0.35);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenScale, setFullscreenScale] = useState(0.6);
  const [iosSave, setIosSave] = useState<IosSaveState | null>(null);
  const [bgDataUrl, setBgDataUrl] = useState<string | null>(null);
  const [editedDescription, setEditedDescription] = useState<string>("");
  const [editedVolunteerInfo, setEditedVolunteerInfo] = useState<string>("");
  const cardRef = useRef<HTMLDivElement>(null);
  const exportCardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { fireConfetti } = useConfetti();

  const isIOSDevice = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroidDevice = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
  const isMobileDevice =
    (typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) ||
    (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 767px)").matches);

  // Create shelter with edited values
  const shelterWithEdits: Shelter = {
    ...shelter,
    description: editedDescription,
    volunteer_info: editedVolunteerInfo,
  };

  // Get dimensions based on format - using dynamic height calculation
  const cardWidth = format === "story" ? PROFILE_STORY_WIDTH : PROFILE_POST_WIDTH;
  const cardHeight = calculateProfileCardHeight(shelterWithEdits, format);
  const [measuredHeight, setMeasuredHeight] = useState<number>(cardHeight);

  // Pre-convert background image to DataURL
  useEffect(() => {
    let cancelled = false;
    imageToDataUrl(shareCardBg)
      .then((url) => {
        if (!cancelled) setBgDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBgDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset state when opening
  useEffect(() => {
    if (!isOpen) return;
    setIosSave(null);
    setEditedDescription(shelter.description || "");
    setEditedVolunteerInfo(shelter.volunteer_info || "");
  }, [isOpen, shelter.id]);

  // Measure actual card height from an unscaled export render (prevents cropping)
  useEffect(() => {
    if (!isOpen) return;
    const node = exportCardRef.current;
    if (!node) return;

    const measure = () => {
      const h = node.offsetHeight;
      if (h && Number.isFinite(h)) setMeasuredHeight(h);
      else setMeasuredHeight(cardHeight);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [isOpen, format, editedDescription, editedVolunteerInfo, bgDataUrl, cardHeight]);

  // Calculate scale based on container and format
  useEffect(() => {
    const updateScale = () => {
      const isLg =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(min-width: 1024px)").matches;

      const availableWidth = isLg ? Math.max(320, window.innerWidth / 2 - 72) : window.innerWidth - 48;
      const maxPreviewWidth = Math.min(560, availableWidth);
      const maxPreviewHeight = window.innerHeight * 0.7;

      const scaleByWidth = maxPreviewWidth / cardWidth;
      const scaleByHeight = maxPreviewHeight / measuredHeight;
      const newScale = Math.min(scaleByWidth, scaleByHeight, 0.7);
      setPreviewScale(newScale);

      const screenWidth = window.innerWidth - 48;
      const screenHeight = window.innerHeight - 100;
      const fullScaleByWidth = screenWidth / cardWidth;
      const fullScaleByHeight = screenHeight / measuredHeight;
      setFullscreenScale(Math.min(fullScaleByWidth, fullScaleByHeight, 1));
    };

    if (isOpen) {
      updateScale();
      setTimeout(updateScale, 100);
    }

    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [isOpen, format, cardWidth, measuredHeight]);

  const getFilename = useCallback(() => {
    const formatSuffix = format === "story" ? "story" : "post";
    return `gdzieSchronisko_profil_${formatSuffix}_${createSlug(shelter.name)}.png`;
  }, [shelter.name, format]);

  const nextPaint = async () => {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  };

  const handleDownloadPng = useCallback(async () => {
    // Always use the dedicated, unscaled export render for reliable export
    const exportNode = exportCardRef.current || cardRef.current;
    if (!exportNode) return;

    setIsGenerating(true);
    try {
      if (!bgDataUrl) {
        try {
          const url = await imageToDataUrl(shareCardBg);
          setBgDataUrl(url);
          await nextPaint();
          await new Promise((resolve) => setTimeout(resolve, 50));
        } catch {
          // continue with original URL
        }
      }

      if (document.fonts?.load) {
        await Promise.all([
          document.fonts.load('400 16px "Nunito"'),
          document.fonts.load('700 16px "Nunito"'),
          document.fonts.load('800 16px "Nunito"'),
        ]);
      }
      await document.fonts.ready;

      await waitForImages(exportNode);
      await new Promise((resolve) => setTimeout(resolve, isMobileDevice ? 500 : 100));
      await nextPaint();

      // Use actual DOM height for dynamic content
      const actualHeight = exportNode.offsetHeight;

      let fontEmbedCSS = "";
      try {
        fontEmbedCSS = await getFontEmbedCSS(exportNode);
      } catch (e) {
        console.warn("[export] getFontEmbedCSS failed:", e);
      }

      const baseOptions = {
        width: cardWidth,
        height: actualHeight,
        pixelRatio: isMobileDevice ? 1 : 1,
        cacheBust: true,
        skipFonts: false,
        fontEmbedCSS,
        backgroundColor: "white",
      };

      // Always use transparent foreground for canvas compositing
      const foregroundOptions = {
        ...baseOptions,
        backgroundColor: "transparent",
        style: { backgroundColor: "transparent" },
        filter: (node: unknown) => {
          const el = node as HTMLElement | null;
          if (!el || typeof (el as any).getAttribute !== "function") return true;
          return el.getAttribute("data-export-bg") !== "true";
        },
      };

      let blob: Blob | null = null;
      const maxAttempts = isMobileDevice ? 3 : 1;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, 350 * attempt));
            await nextPaint();
            await waitForImages(exportNode);
          }
          blob = await toBlob(exportNode, foregroundOptions as any);
          if (blob && blob.size > 1000) break;
        } catch {
          // retry
        }
      }

      if (!blob) {
        const dataUrl = await toPng(exportNode, foregroundOptions as any);
        blob = dataUrlToPngBlobChunked(dataUrl);
      }

      // Always compose background on canvas for reliable export (desktop & mobile)
      if (blob) {
        try {
          const bgSrc = bgDataUrl || (await imageToDataUrl(shareCardBg));
          blob = await composePngOnBackground({
            width: cardWidth,
            height: actualHeight,
            backgroundSrc: bgSrc,
            foregroundBlob: blob,
          });
        } catch (e) {
          console.warn("[export] background compose failed:", e);
        }
      }

      if (!blob) throw new Error("Export failed: blob is null");


      const filename = getFilename();
      const file = new File([blob], filename, { type: blob.type || "image/png" });

      if (isIOSDevice) {
        
        const blobUrl = URL.createObjectURL(blob);
        let shareSupported = false;
        try {
          const navAny = navigator as any;
          shareSupported =
            typeof navAny.share === "function" &&
            (typeof navAny.canShare !== "function" || navAny.canShare({ files: [file] }) === true);
        } catch {
          shareSupported = false;
        }

        setIosSave({ filename, file, blobUrl, shareSupported });
        // Save download info - use description as needs_text for profile graphics
        await saveGraphicDownload(shelter.id, shelter.name, editedDescription || shelter.name, `profile-${format}`);
        
        fireConfetti();
        toast({
          title: "Grafika gotowa 🎉",
          description: shareSupported
            ? "Wybierz Udostępnij → Zapisz obraz"
            : "Przytrzymaj obraz i wybierz 'Zapisz obraz'",
        });
        return;
      }

      // Android: direct download to Downloads folder
      if (isAndroidDevice) {
        
        
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = filename;
        link.href = blobUrl;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
        
        
        // Save download info
        await saveGraphicDownload(shelter.id, shelter.name, editedDescription || shelter.name, `profile-${format}`);
        
        fireConfetti();
        toast({
          title: "Pobrano grafikę 🎉",
          description: "Sprawdź folder Pobrane na swoim telefonie.",
        });
        return;
      }

      // Other mobile: try native share (kept for potential future compatibility)
      if (isMobileDevice && navigator.share) {
        try {
          const canShareFiles = (navigator as any).canShare
            ? (navigator as any).canShare({ files: [file] })
            : true;
          if (canShareFiles) {
            await navigator.share({ files: [file], title: filename });
            
            // Save download info
            await saveGraphicDownload(shelter.id, shelter.name, editedDescription || shelter.name, `profile-${format}`);
            
            fireConfetti();
            toast({
              title: "Udostępniono grafikę 🎉",
              description: "Teraz możesz ją udostępnić na social media.",
            });
            return;
          }
        } catch (shareError) {
          console.log("[profile-export] navigator.share failed, falling back to link download", shareError);
          // fallback below
        }
      }

      console.log("[profile-export] Desktop/fallback path - using link download");
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = filename;
      link.href = blobUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      // Save download info
      await saveGraphicDownload(shelter.id, shelter.name, editedDescription || shelter.name, `profile-${format}`);

      fireConfetti();
      toast({
        title: "Pobrano grafikę 🎉",
        description: "Teraz możesz ją udostępnić na Facebooku lub Instagramie.",
      });
    } catch (error) {
      console.error("[profile-export] ERROR", error);
      toast({
        title: "Błąd",
        description: "Nie udało się wygenerować grafiki. Spróbuj ponownie.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  }, [getFilename, fireConfetti, isIOSDevice, isMobileDevice, bgDataUrl, cardWidth, cardHeight]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 overflow-hidden fixed inset-0 h-[100dvh] w-full max-w-none rounded-none translate-x-0 translate-y-0 top-0 left-0 border-0 z-[2100] bg-background flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Udostępnij profil schroniska
              </DialogTitle>
              <DialogDescription>
                Edytuj teksty i pobierz grafikę do udostępnienia.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left column - Editor */}
              <div className="space-y-4">
                {/* Format selection */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground mr-2">Format:</span>
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={format === "post" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setFormat("post")}
                        >
                          Post (4:5)
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Rozmiar najlepszy dla postów na Facebooku i Instagramie</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={format === "story" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setFormat("story")}
                        >
                          Story (9:16)
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Rozmiar najlepszy dla Instagram Stories</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Description editor */}
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm font-medium">
                    Opis schroniska
                  </Label>
                  <Textarea
                    id="description"
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Opis schroniska..."
                    className="min-h-[100px] md:min-h-[120px] resize-none"
                  />
                </div>

                {/* Volunteer info editor */}
                <div className="space-y-2">
                  <Label htmlFor="volunteerInfo" className="text-sm font-medium">
                    Informacje o wolontariacie
                  </Label>
                  <Textarea
                    id="volunteerInfo"
                    value={editedVolunteerInfo}
                    onChange={(e) => setEditedVolunteerInfo(e.target.value)}
                    placeholder="Informacje o wolontariacie..."
                    className="min-h-[100px] md:min-h-[120px] resize-none"
                  />
                </div>

                {/* Download button */}
                <Button
                  onClick={handleDownloadPng}
                  disabled={isGenerating}
                  className="w-full gap-2"
                  size="lg"
                >
                  <Download className="w-4 h-4" />
                  {isGenerating ? "Generowanie..." : "Pobierz PNG"}
                </Button>

              </div>

              {/* Right column - Preview */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Podgląd:</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsFullscreen(true)}
                    className="gap-1 text-xs"
                  >
                    <Maximize2 className="w-3 h-3" />
                    Powiększ
                  </Button>
                </div>

                <div
                  ref={containerRef}
                  className="relative overflow-hidden rounded-lg shadow-md mx-auto"
                  style={{
                    width: cardWidth * previewScale,
                    height: measuredHeight * previewScale,
                  }}
                >
                  <div
                    ref={cardRef}
                    className="absolute top-0 left-0 origin-top-left"
                    style={{
                      width: cardWidth,
                      height: measuredHeight,
                      transform: `scale(${previewScale})`,
                    }}
                  >
                    <ShareProfileCard
                      ref={cardRef}
                      shelter={shelterWithEdits}
                      backgroundDataUrl={bgDataUrl || undefined}
                      format={format}
                    />
                  </div>
                </div>

                {/* Hidden export render (full size, no scaling). Critical for iOS/Android export reliability. */}
                <div
                  aria-hidden
                  className="fixed left-0 top-0 pointer-events-none opacity-0"
                  style={{
                    width: cardWidth,
                    zIndex: -1,
                  }}
                >
                  <ShareProfileCard
                    ref={exportCardRef}
                    shelter={shelterWithEdits}
                    backgroundDataUrl={bgDataUrl || undefined}
                    format={format}
                  />
                </div>
              </div>
            </div>

            {/* Info */}
            <p className="text-xs text-muted-foreground text-center pt-4">
              Pobierz grafikę i wrzuć na Facebooka/Instagrama — udostępnienie zwiększa zasięg.
            </p>
          </div>
        </ScrollArea>

        {/* Fullscreen modal */}
        {isFullscreen && (
          <div
            className="fixed inset-0 z-[2100] bg-black/90 flex items-center justify-center p-6"
            onClick={() => setIsFullscreen(false)}
          >
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            >
              <X className="w-6 h-6" />
            </button>

            <div
              className="relative overflow-hidden rounded-lg shadow-2xl"
              style={{
                width: cardWidth * fullscreenScale,
                height: measuredHeight * fullscreenScale,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="absolute top-0 left-0 origin-top-left"
                style={{
                  width: cardWidth,
                  height: measuredHeight,
                  transform: `scale(${fullscreenScale})`,
                }}
              >
                <ShareProfileCard
                  shelter={shelterWithEdits}
                  backgroundDataUrl={bgDataUrl || undefined}
                  format={format}
                />
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {/* iOS Save Dialog - separate modal for better UX */}
      <Dialog
        open={!!iosSave}
        onOpenChange={(open) => {
          if (!open) {
            if (iosSave?.blobUrl) URL.revokeObjectURL(iosSave.blobUrl);
            setIosSave(null);
          }
        }}
      >
        <DialogContent className="max-w-md z-[2200]">
          <DialogHeader>
            <DialogTitle>Zapisz grafikę</DialogTitle>
            <DialogDescription>
              {isAndroidDevice 
                ? 'Przytrzymaj podgląd grafiki i wybierz „Pobierz obraz".'
                : 'Przytrzymaj podgląd grafiki i wybierz „Zapisz w Zdjęciach".'}
            </DialogDescription>
          </DialogHeader>

          {iosSave && (
            <div className="rounded-lg overflow-hidden border bg-background">
              <img
                src={iosSave.blobUrl}
                alt={`Podgląd ${iosSave.filename}`}
                className="w-full h-auto"
                loading="eager"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
